import { t } from '../i18n/lingua.js';
/**
 * Il modo in cui il frontend parla con l'API.
 *
 * Una funzione sola, perché gli errori del servizio hanno tutti la stessa
 * forma — `{ errore, messaggio }` — e vanno mostrati come sono: il messaggio
 * è già scritto per essere letto da una persona, e riscriverlo qui vorrebbe
 * dire tradurre due volte la stessa cosa.
 */

export class ErroreApi extends Error {
  constructor(
    readonly codice: string,
    messaggio: string,
    readonly stato: number,
    /**
     * Il resto della risposta. Alcuni errori portano dettagli che servono a
     * video — quali campi mancano, quali righe sono rimaste indietro — e
     * buttarli via costringerebbe a indovinarli.
     */
    readonly dettagli: Record<string, unknown> = {},
  ) {
    super(messaggio);
    this.name = 'ErroreApi';
  }
}

export async function chiama<T>(
  percorso: string,
  opzioni: { metodo?: string; corpo?: unknown } = {},
): Promise<T> {
  let risposta: Response;
  try {
    risposta = await fetch(percorso, {
      method: opzioni.metodo ?? 'GET',
      // il cookie di sessione deve viaggiare
      credentials: 'same-origin',
      headers: opzioni.corpo === undefined ? {} : { 'content-type': 'application/json' },
      body: opzioni.corpo === undefined ? undefined : JSON.stringify(opzioni.corpo),
    });
  } catch {
    throw new ErroreApi(
      'servizioIrraggiungibile',
      'Il servizio non risponde. Controlla di essere in rete e riprova.',
      0,
    );
  }

  if (risposta.status === 204) return undefined as T;

  const corpo = await risposta.json().catch(() => null);

  if (!risposta.ok) {
    // Il servizio manda { error, message } con il messaggio già scritto per
    // essere letto: riscriverlo qui vorrebbe dire tradurre due volte la
    // stessa cosa, e mostrare "qualcosa non ha funzionato" al posto di
    // "questo campo è più lungo di 50 caratteri" non aiuta nessuno.
    throw new ErroreApi(
      corpo?.error ?? 'erroreSconosciuto',
      corpo?.message ?? t('Qualcosa non ha funzionato.'),
      risposta.status,
      (corpo ?? {}) as Record<string, unknown>,
    );
  }

  return corpo as T;
}
